// ============================================================================
// Fragments partages par les 2 surfaces du profil (L16) — /profile et /profile/:email
// ============================================================================

import { useState } from 'react';
import { EmptyState } from '@gs/ui';
import { ChevronDown, ShieldAlert } from 'lucide-react';
import { useTranslation } from '../../i18n';

/**
 * Refus d'acces (fail-closed corporate Q1, ou standing GEST absent). L'AUTORITE
 * est le 403 serveur — cet ecran n'est que l'UX du refus. Meme forme que
 * TeamsForbidden (E1b) : un EmptyState DANS le shell, pas le GsForbiddenScreen
 * plein ecran (qui est l'ecran de deconnexion du gate d'app).
 */
export function ProfileForbidden() {
  const { t } = useTranslation();
  return (
    <EmptyState
      icon={<ShieldAlert size={48} aria-hidden="true" />}
      title={t('gest:profile.forbidden.title')}
      description={t('gest:profile.forbidden.message')}
    />
  );
}

/**
 * F-804/F-805 : bloc repliable « inline », REPLIE PAR DEFAUT (parite PoC). @gs/ui
 * n'a pas de primitive Collapsible et packages/ui est interdit a ce frag -> <details>
 * natif (accessible, zero JS d'etat, ferme par defaut).
 */
export function Collapsible({ summary, children }: { summary: string; children: React.ReactNode }) {
  const [open, setOpen] = useState(false);
  return (
    <details
      open={open}
      onToggle={(e) => setOpen((e.currentTarget as HTMLDetailsElement).open)}
      className="group"
    >
      <summary className="flex w-fit cursor-pointer touch-manipulation list-none items-center gap-1.5 text-meta font-medium text-slate-500 transition-colors hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200">
        <ChevronDown
          size={14}
          aria-hidden="true"
          className={`transition-transform ${open ? 'rotate-180' : ''}`}
        />
        {summary}
      </summary>
      <div className="mt-3">{children}</div>
    </details>
  );
}

/**
 * Onglet dont la GARDE est portee et prouvee (matrice M-801) mais dont le CONTENU
 * vit hors du manifeste L16 :
 *   - `pulse`      -> MemberPulseHistory (components/employee/*, hors lot)
 *   - `objectives` -> ObjectivesBoard (idem)
 *   - `documents`  -> EmployeeDocumentsSection (idem) + bucket Firebase non migre
 *   - `management` -> ManagementTab (L09)
 * On rend un EmptyState honnete plutot que d'inventer un ecran hors contrat.
 */
export function ComingSoonTab({ icon, lot }: {
  icon: React.ReactNode;
  lot: 'pulse' | 'objectives' | 'documents' | 'management';
}) {
  const { t } = useTranslation();
  return (
    <EmptyState
      icon={icon}
      title={t(`gest:profile.comingSoon.${lot}.title`)}
      description={t(`gest:profile.comingSoon.${lot}.description`)}
    />
  );
}
